import { z } from 'zod';
import { v1PaginationQuerySchema, v1PaginationSchema } from './pagination';
import { csvParam, v1SortOrderSchema } from './query';
import { v1UserRefSchema } from './actor';
/**
 * Public Alerts resource for `/api/v1` (KIR-4376).
 *
 * Every field here is frozen for the life of `v1`: it may gain optional
 * siblings, never lose or rename one. That is why this file restates the shape
 * instead of re-exporting `contracts/alerts.ts` — the internal grouped-alert
 * contract carries roughly forty fields that exist to drive one specific table
 * UI, several of them flag-gated and several more (match context, entropy,
 * endpoint identifiers) deliberately not customer-visible. Anything not listed
 * below cannot leak through the DTO mapper, because the mapper never spreads.
 */
/** Public severity vocabulary. A closed set; matches the product's four levels. */
export const v1AlertSeveritySchema = z
    .enum(['low', 'medium', 'high', 'critical'])
    .meta({
    id: 'V1AlertSeverity',
    title: 'Alert severity',
    description: "How serious the finding is. The product's four levels.",
});
/**
 * What the system did about the alert. `unknown` covers findings ingested
 * before outcome reporting existed, and is a real value rather than a null so
 * a caller's switch stays exhaustive.
 */
export const v1AlertOutcomeSchema = z
    .enum(['prevented', 'detected', 'unknown'])
    .meta({
    id: 'V1AlertOutcome',
    title: 'Alert outcome',
    description: 'What the system did about the alert. `prevented` means the action was blocked; `detected` means it was observed and reported. `unknown` covers findings ingested before outcome reporting existed — a real value rather than a null, so an exhaustive switch stays exhaustive.',
});
/**
 * Suppression state collapsed to one value for callers that only need to know
 * whether an alert is live.
 *
 * Precedence when an alert is both muted and excluded: `excluded` wins, because
 * exclusion is the stronger statement (it suppresses future enforcement, not
 * just notification). The `mute` and `exclusion` objects are still both present
 * on the alert, so nothing is lost by the collapse.
 */
export const v1AlertStatusSchema = z
    .enum(['active', 'muted', 'excluded'])
    .meta({
    id: 'V1AlertStatus',
    title: 'Alert status',
    description: 'Suppression state collapsed to one value. When an alert is both muted and excluded, `excluded` wins — exclusion is the stronger statement. The `mute` and `exclusion` objects are still both present, so nothing is lost by the collapse.',
});
/** Which alerts a list call should return. Mirrors {@link v1AlertStatusSchema} plus `all`. */
// Deliberately unnamed, unlike its sibling enums: this one is only ever a query
// parameter, and a named schema is emitted as a bare `$ref`, which carries no
// room for the use-site description. Inlining keeps the explanation on the
// parameter, where the reader of the docs actually meets it.
export const v1AlertStatusFilterSchema = z.enum([
    'active',
    'suppressed',
    'all',
]);
/**
 * A user an alert was seen for, with that user's share of the occurrences.
 * Counts are scoped to the same filters the list call applied.
 */
export const v1AlertUserSchema = v1UserRefSchema
    .extend({
    occurrenceCount: z
        .number()
        .int()
        .min(0)
        .describe("This user's share of the alert's occurrences, scoped to the same filters the list call applied."),
    lastSeenAt: z
        .string()
        .datetime()
        .nullable()
        .describe('When this user last triggered the alert (ISO 8601 UTC).'),
})
    .meta({
    id: 'V1AlertUser',
    title: 'Alert user',
    description: "A user the alert was seen for, with that user's share of the occurrences.",
});
/**
 * Mute state. `muted` is evaluated at read time against `mutedUntil`, so an
 * expired timed mute reports `muted: false` without waiting for a sweeper.
 */
export const v1AlertMuteSchema = z
    .object({
    muted: z
        .boolean()
        .describe('Whether the mute is in force right now. Evaluated at read time against `mutedUntil`, so an expired timed mute reports `false` without waiting for a sweeper.'),
    mutedAt: z
        .string()
        .datetime()
        .nullable()
        .describe('When the mute began (ISO 8601 UTC). Read this to tell a fresh mute from a replayed one, since both answer 200.'),
    /** Null with `muted: true` means muted indefinitely. */
    mutedUntil: z
        .string()
        .datetime()
        .nullable()
        .describe('When the mute expires (ISO 8601 UTC). Null alongside `muted: true` means muted indefinitely.'),
    /** Null when the muting user has since been deleted. */
    mutedBy: v1UserRefSchema
        .nullable()
        .describe('Who muted it. Null if that user has since been deleted.'),
})
    .meta({
    id: 'V1AlertMute',
    title: 'Mute state',
    description: 'Whether an alert is muted, until when, and by whom.',
});
/** Exclusion state. Mirror of {@link v1AlertMuteSchema}; same expiry semantics. */
export const v1AlertExclusionSchema = z
    .object({
    excluded: z
        .boolean()
        .describe('Whether the exclusion is in force right now. Same read-time evaluation as `mute.muted`.'),
    excludedAt: z
        .string()
        .datetime()
        .nullable()
        .describe('When the exclusion began (ISO 8601 UTC).'),
    /** Null with `excluded: true` means excluded indefinitely. */
    excludedUntil: z
        .string()
        .datetime()
        .nullable()
        .describe('When the exclusion expires (ISO 8601 UTC). Null alongside `excluded: true` means excluded indefinitely.'),
    excludedBy: v1UserRefSchema
        .nullable()
        .describe('Who excluded it. Null if that user has since been deleted.'),
})
    .meta({
    id: 'V1AlertExclusion',
    title: 'Exclusion state',
    description: 'Whether an alert is excluded, until when, and by whom. Exclusion suppresses future enforcement, not just notification.',
});
/**
 * One alert.
 *
 * `id` is stable for the life of the underlying finding identity, so it is safe
 * to store in an external system and use as the mute/exclusion path segment.
 *
 * `mute` / `exclusion` are `null` — not absent — when the corresponding feature
 * is not enabled for the organization. A nullable object is one shape a client
 * must handle; an optional one is two.
 */
export const v1AlertSchema = z
    .object({
    id: z
        .string()
        .describe('Stable for the life of the underlying finding, so it is safe to store in an external system. Also the path segment for the mute and exclusion endpoints.'),
    type: z
        .string()
        .describe('The kind of finding, e.g. `auto_run` or `hidden_characters`.'),
    severity: v1AlertSeveritySchema,
    description: z.string().describe('Human-readable summary of the finding.'),
    status: v1AlertStatusSchema,
    outcome: v1AlertOutcomeSchema,
    firstSeenAt: z
        .string()
        .datetime()
        .describe('When this alert was first observed (ISO 8601 UTC).'),
    lastSeenAt: z
        .string()
        .datetime()
        .describe('When this alert was most recently observed (ISO 8601 UTC).'),
    occurrenceCount: z
        .number()
        .int()
        .min(0)
        .describe('How many times the finding has been observed.'),
    /** Null for alerts with no file (chat content, process activity). */
    filePath: z
        .string()
        .nullable()
        .describe('Path of the file the finding is in. Null for alerts with no file, such as chat content or process activity.'),
    /** 0–100 risk score derived from severity. Null when not computed. */
    score: z
        .number()
        .int()
        .min(0)
        .max(100)
        .nullable()
        .describe('Risk score from 0 to 100, derived from severity. Null when not computed.'),
    /** Kirin policy that produced the alert. Null for legacy unmapped types. */
    policyKey: z
        .string()
        .nullable()
        .describe('The Kirin policy that produced the alert. Null for legacy findings that predate policy mapping.'),
    subPolicyKey: z
        .string()
        .nullable()
        .describe('The specific rule within that policy, when the policy has several.'),
    /** IDEs / agent platforms this alert was seen on, sorted, may be empty. */
    platforms: z
        .array(z.string())
        .describe('IDEs and agent platforms the alert was seen on, sorted. May be empty. These are the values the `platform` filter accepts.'),
    users: z
        .array(v1AlertUserSchema)
        .describe('The users this alert was seen for, with per-user occurrence counts.'),
    mute: v1AlertMuteSchema
        .nullable()
        .describe('Mute state, or null when muting is not enabled for the organization. Null rather than absent, so there is one shape to handle instead of two.'),
    exclusion: v1AlertExclusionSchema
        .nullable()
        .describe('Exclusion state, or null when exclusions are not enabled for the organization.'),
})
    .meta({
    id: 'V1Alert',
    title: 'Alert',
    description: 'One security finding raised for your organization, aggregated across every occurrence of the same underlying issue.',
});
/** Sortable fields. Every sort is made total by an internal id tie-break, so repeated calls return the same order. */
export const v1AlertSortFieldSchema = z
    .enum(['lastSeenAt', 'firstSeenAt', 'severity', 'occurrenceCount'])
    .describe('Field to sort by. Every sort carries an internal tie-break, so repeated calls return the same order.');
/**
 * `GET /api/v1/organizations/:organizationId/alerts` query.
 *
 * Filters are the ones the matching UI screen offers (parent AC #2): severity,
 * outcome, user, file, IDE/platform, first seen, last seen. Each is optional and
 * ANDed with the rest; multi-value filters are ORed within themselves.
 */
export const v1AlertListQuerySchema = v1PaginationQuerySchema.extend({
    severity: csvParam('Keep only alerts at these severities.'),
    outcome: csvParam('Keep only alerts with these outcomes.'),
    /** Kirin user ids, as returned in `users[].id`. */
    userId: csvParam('Keep only alerts seen for these Kirin user ids, as returned in `users[].id`.'),
    /** Substring match on the alert's file path. */
    filePath: z
        .string()
        .min(1)
        .optional()
        .describe('Keep only alerts whose file path contains this substring.'),
    /** IDE / agent platform names, as returned in `platforms`. */
    platform: csvParam('Keep only alerts seen on these IDEs or agent platforms, as returned in `platforms`.'),
    /**
     * Date bounds are all **exclusive** — `firstSeenAfter` matches alerts first
     * seen strictly after the instant given. Exclusive in both directions, and on
     * both fields, so a caller paging a window by feeding the last result's
     * timestamp back in never re-reads the boundary row.
     */
    firstSeenAfter: z
        .string()
        .datetime()
        .optional()
        .describe('Keep only alerts first seen strictly after this instant (ISO 8601). Exclusive, so paging a window by feeding the last result back in never re-reads the boundary row.'),
    firstSeenBefore: z
        .string()
        .datetime()
        .optional()
        .describe('Keep only alerts first seen strictly before this instant (ISO 8601).'),
    lastSeenAfter: z
        .string()
        .datetime()
        .optional()
        .describe('Keep only alerts last seen strictly after this instant (ISO 8601).'),
    lastSeenBefore: z
        .string()
        .datetime()
        .optional()
        .describe('Keep only alerts last seen strictly before this instant (ISO 8601).'),
    /**
     * Which alerts to return. Defaults to `active`.
     *
     * `suppressed` returns an **empty page** for an organization that has neither
     * muting nor exclusions enabled. That is not an error: with no suppression
     * mechanism available, no alert can be suppressed, and the empty page is
     * indistinguishable from an organization that simply has nothing suppressed
     * right now. Enabling either feature makes the same query start returning
     * rows, with no client change.
     */
    status: v1AlertStatusFilterSchema
        .default('active')
        .describe('Which alerts to return. `suppressed` returns an empty page for an organization with neither muting nor exclusions enabled: with no suppression mechanism available, nothing can be suppressed.'),
    sortBy: v1AlertSortFieldSchema.default('lastSeenAt'),
    sortOrder: v1SortOrderSchema.default('desc'),
});
export const v1AlertListResponseSchema = z
    .object({
    data: z.array(v1AlertSchema).describe('This page of alerts.'),
    pagination: v1PaginationSchema,
})
    .meta({
    id: 'V1AlertList',
    title: 'Alert list',
    description: 'A page of alerts, plus where it sits in the full result set.',
});
/**
 * A suppression deadline: an ISO instant strictly in the future, or null/absent
 * for "indefinitely".
 *
 * **Strictly** in the future, with no backwards tolerance — deliberately
 * stricter than the internal `muteAlertRequestSchema`, which forgives 5s of
 * client→server clock skew.
 *
 * That tolerance is right for the web app, whose picker only ever produces
 * deadlines minutes or hours out, so the slack purely absorbs drift on a value
 * that was always genuinely in the future. It is wrong here. Suppression is
 * active only while `until > now()`, evaluated when the row is read back, so a
 * deadline inside the tolerance window writes a row and then reports
 * `muted: false` / `excluded: false`. The caller gets a 200 that says the thing
 * it just asked for is not in effect, and cannot tell that from a no-op.
 *
 * Rejecting is strictly more useful than accepting there: the mute the caller
 * asked for has already elapsed either way, and a 400 naming the field says so.
 * With this rule a 2xx from a suppression write means the suppression is in
 * force. (A deadline a millisecond ahead can still expire in the round trip —
 * that race is inherent to any absolute-deadline API — but the guaranteed
 * multi-second dead zone is gone.)
 */
const futureOrNull = (field) => z
    .string()
    .datetime()
    .nullable()
    .optional()
    .refine(value => value === null ||
    value === undefined ||
    new Date(value).getTime() > Date.now(), {
    message: `${field} must be strictly in the future; a deadline that has already passed would write a suppression that is immediately inactive`,
});
/**
 * `POST .../alerts/:alertId/mute` body.
 *
 * Every field is optional so `{}` is a valid request — a new **required**
 * request field would be a breaking change, and the contract snapshot test
 * enforces that. Omitting `mutedUntil` means "mute indefinitely".
 */
export const v1MuteAlertRequestSchema = z
    .object({
    mutedUntil: futureOrNull('mutedUntil').describe('When the mute should expire (ISO 8601). Must be strictly in the future. Omit or send null to mute indefinitely.'),
})
    .meta({
    id: 'V1MuteAlertRequest',
    title: 'Mute request',
    description: 'Every field is optional, so `{}` is a valid request that mutes indefinitely. Safe to retry: muting an already-muted alert returns 200 with the mute currently in force rather than a conflict, and does not re-time it. To change an active window, remove the mute first.',
    // A deliberately far-off date, not a plausible one: the reference page
    // prefills its "Try it" body from here, and `futureOrNull` rejects anything
    // not strictly in the future — so a nearby date turns the documented
    // example into a 400 the moment it elapses.
    examples: [{ mutedUntil: '2099-12-31T23:59:59Z' }, {}],
});
/** `POST .../alerts/:alertId/exclusions` body. Mirror of the mute request. */
export const v1CreateAlertExclusionRequestSchema = z
    .object({
    excludedUntil: futureOrNull('excludedUntil').describe('When the exclusion should expire (ISO 8601). Must be strictly in the future. Omit or send null to exclude indefinitely.'),
})
    .meta({
    id: 'V1CreateAlertExclusionRequest',
    title: 'Exclusion request',
    description: 'Mirror of the mute request, with the same retry semantics: creating an exclusion on an already-excluded alert returns 200 with the exclusion in force.',
    examples: [{ excludedUntil: '2099-12-31T23:59:59Z' }, {}],
});
/**
 * What a mute / exclusion write returns: the alert id plus the resulting state.
 *
 * The state, not just an acknowledgement, because both writes are idempotent —
 * a retry that finds the alert already muted returns 200 with the state that is
 * actually in force, and a caller needs to be able to tell the two apart by
 * reading `mutedAt` rather than by the status code.
 */
export const v1AlertMuteResponseSchema = z
    .object({
    alertId: z.string().describe('The alert that was muted.'),
    mute: v1AlertMuteSchema.describe('The mute now in force.'),
})
    .meta({
    id: 'V1AlertMuteResult',
    title: 'Mute result',
    description: 'The resulting state rather than a bare acknowledgement, because the write is idempotent: read `mute.mutedAt` to tell a fresh mute from a replayed one.',
});
export const v1AlertExclusionResponseSchema = z
    .object({
    alertId: z.string().describe('The alert that was excluded.'),
    exclusion: v1AlertExclusionSchema.describe('The exclusion now in force.'),
})
    .meta({
    id: 'V1AlertExclusionResult',
    title: 'Exclusion result',
    description: 'The resulting state rather than a bare acknowledgement. Read `exclusion.excludedAt` to tell a fresh exclusion from a replayed one.',
});
//# sourceMappingURL=alerts.js.map